// The client failure taxonomy (TASK.md "The API client error model"). Every network call in
// the app resolves to exactly one of these five, thrown by the one client door
// (src/lib/api/client.ts) and switched on by the one failure handler
// (src/lib/api/failure-handler.ts) — never inspected ad hoc at the call site.

/** 4xx/5xx with a body that parses as `errorEnvelopeSchema` — the common case; the API always
 * speaks this shape for its own rejections. `details` is the envelope's own structured extras
 * (parts.md's guard evidence: referencing country codes, a duplicate slug's current label, the
 * forbidden constructs found, a reorder delta) — carried through verbatim, `undefined` when the
 * envelope didn't send any. Callers render it where a surface exists (TASK.md client contract);
 * this class only carries it, never interprets it. */
export class EnvelopeError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly details: unknown;

  constructor(code: string, message: string, requestId: string, details?: unknown) {
    super(message);
    this.name = "EnvelopeError";
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

/** The request never reached a server at all — offline, DNS failure, or our own timeout. No
 * requestId can exist; nothing was assigned one. */
export class NetworkError extends Error {
  constructor(message = "The request could not reach the server.") {
    super(message);
    this.name = "NetworkError";
  }
}

/** A non-ok response whose body is not a valid envelope — infrastructure in front of the API
 * (a proxy/edge 502/503/504 HTML page), not the API's own rejection. */
export class NonEnvelopeServerError extends Error {
  readonly requestId: string | undefined;

  constructor(requestId: string | undefined, message = "The server returned an unexpected error.") {
    super(message);
    this.name = "NonEnvelopeServerError";
    this.requestId = requestId;
  }
}

/** 401 with NO token sent — the session is gone or was never there. Normal life, not an error:
 * the redirect to /sign-in IS the honest state. */
export class UnauthenticatedError extends Error {
  constructor() {
    super("Authentication required.");
    this.name = "UnauthenticatedError";
  }
}

/**
 * 401 WITH a token sent — Clerk minted a session, the API still refuses it. The API says only
 * 401 by design (apps/api/src/auth/middleware.ts: unknown_staff, invalid_token, and a bad azp
 * all collapse to one bare rejection so an attacker learns nothing), so the client cannot know
 * which — but it knows the difference that matters: a redirect to /sign-in is WRONG here.
 * Clerk would see its own valid session, send the browser straight back, and the two would
 * bounce forever (observed in production, 2026-08-02 — the loop this class exists to end).
 * A signed-in identity the system won't accept is a full stop, exactly like an archived one.
 */
export class UnknownStaffError extends Error {
  constructor() {
    super("This account is not recognized as staff.");
    this.name = "UnknownStaffError";
  }
}

/** 403 auth.staff_archived — the full stop. No retry loop; the account has been archived. */
export class StaffArchivedError extends Error {
  constructor() {
    super("This staff account has been archived.");
    this.name = "StaffArchivedError";
  }
}

export type ApiError =
  | EnvelopeError
  | NetworkError
  | NonEnvelopeServerError
  | UnauthenticatedError
  | UnknownStaffError
  | StaffArchivedError;
